import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {useHistory} from "react-router-dom";
import {useFormik, FormikProps} from 'formik';
import _ from 'lodash';
import {
  addEditPriceQuote,
  getPriceQuoteDetails,
  uploadPriceQuoteFile,
  deletePriceQuoteFile,
  downloadPriceQuoteFile,
  previewPriceQuoteFile,
} from '../_redux/actions/priceQuoteDetailsActions';
import {priceQuoteStatus} from '../../_common/constants/options';
import {filterOptions} from '../../_common/utils/options';
import {getContactData} from '../utils/getContactData';
import {getFirstGiClient} from '../utils/getFirstGiClient';
import {usePageContext} from './PageContext';
import {Customer, Contact} from "../../_common/types";
import {PriceQuoteDetails, PriceQuoteFile, Line} from "../types";
import {optionsVatType} from "../../CustomerDetails/constants/options";
import {useCalculations} from "../components/CalculationHook/useCalculations";

type FormValues = {
  inputValue: string;
  customers: Customer[];
  priceQuoteID: number;
  status: any;
  files: PriceQuoteFile[];
  createDate: string;
  contacts: Contact[];
  showCreateLinkModal: boolean;
  lines: Line[];
  totalWithoutVat: string;
  totalVat: string;
  totalWithVat: string;
};

type ContextType = {
  isEdit: boolean;
  formik: FormikProps<FormValues>;
  submit: (showLinkModal: boolean) => Promise<void>;
  uploadFiles: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteFile: (fileID: number, indexToDelete: number) => void;
  handleDownloadFile: (fileID: number, fileName: string) => void;
  handlePreviewFile: (fileID: number) => Promise<string>;
  getFileFromGI: (fileName: string, url: string) => Promise<void>;
  loadingFile: boolean;
  setLoadingFile: React.Dispatch<React.SetStateAction<boolean>>;
  loadingData: boolean;
  customers: Customer[];
  greenInvoiceClientID: string | null;
  isPriceQuoteComplete: boolean;
  orderID: number;
  switchToSecondTab: () => void;
  tabsRef: any;
};

const PriceQuoteDetailsContext = createContext<ContextType | undefined>(undefined);

export const usePriceQuoteDetailsContext = (): ContextType => {
  const ctx = useContext(PriceQuoteDetailsContext);
  if (!ctx) {
    throw new Error('usePriceQuoteDetailsContext must be used inside provider');
  }
  return ctx;
};

export function PriceQuoteDetailsProvider({children}: { children: ReactNode }) {
  const history = useHistory();
  const {priceQuoteID, setShowLinkModal, setIsTab1Valid, setIsTab3Valid} = usePageContext();
  const isEdit = priceQuoteID !== -1;
  const defaultDetails = {
    priceQuote: {
      contactAddress: '',
      contactDeleted: 0,
      contactEmail: '',
      contactFirstName: '',
      contactID: 0,
      contactJob: '',
      contactLastName: '',
      contactOrganization: '',
      contactPhone: '',
      createDate: '',
      customers: [],
      orderID: 0,
      priceQuoteID: 0,
      priceQuoteStatusID: 0,
      priceQuoteStatusName: ''
    },
    lines: [],
    fileList: []
  };
  const [data, setData] = useState<PriceQuoteDetails['priceQuote']>(defaultDetails.priceQuote);
  const [lines, setLines] = useState<PriceQuoteDetails['lines']>(defaultDetails.lines);
  const [files, setFiles] = useState<PriceQuoteDetails['fileList']>(defaultDetails.fileList);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [giState, setGiState] = useState<{ oldFile?: PriceQuoteFile; }>({});

  const getData = async () => {
    setLoadingData(true);
    try {
      const details = await getPriceQuoteDetails(priceQuoteID);
      if (details?.priceQuote) setData(details.priceQuote);
      if (details?.lines) setLines(details.lines);
      if (details?.fileList) setFiles(details.fileList);
    } finally {
      setLoadingData(false);
    }
  };
  useEffect(() => {
    getData();
  }, [priceQuoteID]);

  const formik = useFormik<FormValues>({
    initialValues: {
      inputValue: '',
      customers: _.get(data, 'customers', []),
      priceQuoteID,
      status: isEdit
        ? filterOptions(_.get(data, 'priceQuoteStatusID'))(priceQuoteStatus)?.[0]
        : priceQuoteStatus[0],
      files,
      createDate: _.get(data, 'createDate', ''),
      contacts: _.get(data, 'contactID')
        ? [{
          contactID: _.get(data, 'contactID', 0),
          firstName: _.get(data, 'contactFirstName', ''),
          lastName: _.get(data, 'contactLastName', ''),
          phone: _.get(data, 'contactPhone', ''),
          email: _.get(data, 'contactEmail', ''),
          deleted: _.get(data, 'contactDeleted', 0),
          address: _.get(data, 'contactAddress', ''),
          job: _.get(data, 'contactJob', ''),
          organization: _.get(data, 'contactOrganization', ''),
          statusID: _.get(data, 'contactStatusID', 0)
        }]
        : [],
      showCreateLinkModal: true,
      lines: Array.isArray(lines) && lines.length > 0
        ? lines.map(item => {
          const {vatType, ...rest} = item;
          return {vatType: filterOptions(vatType)(optionsVatType)[0], ...rest}
        })
        : [],
      totalWithoutVat: '',
      totalVat: '',
      totalWithVat: _.get(data, 'price', ''),
    },
    enableReinitialize: true,
    onSubmit: () => {
    },
    validateOnMount: true,
    validate: (values) => {
      const errors: any = {};
      if (!values.customers || values.customers.length === 0) {
        errors.customers = 'At least one customer is required';
      }

      // Lines
      if (values.lines && values.lines.length > 0) {
        const lineErrors = values.lines.map((line) => {
          const error: any = {};
          if (!line.description?.trim()) {
            error.description = 'Description is required';
          }

          if (!line.quantity) {
            error.quantity = 'Quantity is required';
          }

          if (!line.price) {
            error.price = 'Price is required';
          }

          if (!line.vatType) {
            error.vatType = 'VAT type is required';
          }

          return Object.keys(error).length ? error : undefined;
        });

        if (lineErrors.some(Boolean)) {
          errors.lines = lineErrors;
        }
      }
      return errors;
    }
  });

  const filesRef = useRef<PriceQuoteFile[]>(formik.values.files);
  useEffect(() => {
    filesRef.current = formik.values.files;
  }, [formik.values.files]);

  const {total} = useCalculations(formik.values.lines);

  const giStateRef = useRef(giState);
  useEffect(() => {
    giStateRef.current = giState;
  }, [giState]);

  const isPriceQuoteComplete = formik.values.status?.value === 3;

  const getFileFromGI = async (fileName: string, url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();

    const file = new File([blob], `${fileName}.pdf`, {
      type: blob.type,
    }) as PriceQuoteFile;

    const current = filesRef.current ?? [];

    const oldGI = current.find(f => Number(f.isGreenInvoiceFile) === 1);

    if (oldGI?.fileID) {
      setGiState({oldFile: oldGI});
    }

    const withoutGI = current.filter(f => Number(f.isGreenInvoiceFile) !== 1);

    file.isGreenInvoiceFile = 1;

    formik.setFieldValue('files', [...withoutGI, file], false);
  };

  const uploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploaded = Array.from(e.target.files || []);
    if (!uploaded.length) return;

    setLoadingFile(true);

    formik.setFieldValue('files', [
      ...(filesRef.current || []),
      ...uploaded,
    ]);

    setTimeout(() => {
      setLoadingFile(false);
      document.body.click();
    }, 2000);

    e.target.value = '';
  };

  const submit = async (showLinkModal: boolean) => {
    const {contactID} = getContactData(formik.values.contacts);
    const preparedLines = formik.values.lines.map(item => {
      const {vatType, price, lineID, ...rest} = item;
      return {
        vatType: typeof vatType === 'object' ? vatType?.value : vatType,
        price: price ? parseFloat(price) : 0,
        ...rest
      }
    })

    const payload = {
      priceQuoteID: formik.values.priceQuoteID,
      status: formik.values.status,
      customers: formik.values.customers,
      contactID,
      lines: preparedLines,
      totalWithVat: total ?? 0,
    };

    const newID = await addEditPriceQuote(payload);

    const finalPriceQuoteID = isEdit
      ? formik.values.priceQuoteID
      : newID;

    if (!finalPriceQuoteID) return;

    const allFiles = formik.values.files || [];

    const filesToUpload = allFiles.filter(f => !f.fileID);

    const giIndex = filesToUpload.findIndex(
      f => Number(f.isGreenInvoiceFile) === 1,
    );

    const giFile = giIndex >= 0 ? filesToUpload[giIndex] : null;

    const formData = new FormData();
    formData.append('priceQuoteID', finalPriceQuoteID.toString());

    filesToUpload.forEach(file => {
      formData.append('filesToSave', file);
    });

    if (giFile) {
      formData.append('giFileName', giFile.name);
    }

    if (filesToUpload.length) {
      await uploadPriceQuoteFile(formData);

      if (giStateRef.current.oldFile?.fileID) {
        await deletePriceQuoteFile(giStateRef.current.oldFile.fileID);
      }

      setGiState({});
    }

    if (isEdit) {
      await getData();
    } else {
      history.push(`/price-quote-details/${finalPriceQuoteID}`);
    }

    if (
      formik.values.showCreateLinkModal &&
      !isPriceQuoteComplete &&
      showLinkModal
    ) {
      setShowLinkModal(true);
    }
  };

  const handleDeleteFile = (fileID: number, indexToDelete: number) => {
    if (fileID) {
      deletePriceQuoteFile(fileID).then(getData);
    } else {
      formik.setFieldValue(
        'files',
        (formik.values.files || []).filter((_, i) => i !== indexToDelete),
      );
    }
  };

  const handleDownloadFile = (fileID: number, fileName: string) => downloadPriceQuoteFile(priceQuoteID, fileID, fileName);

  const handlePreviewFile = (fileID: number) => previewPriceQuoteFile(priceQuoteID, fileID);

  const tabsRef = useRef('');
  // @ts-ignore
  const switchToSecondTab = () => tabsRef.current?.setActiveTab('inspections');

  useEffect(() => {
    if (formik.errors?.customers) {
      setIsTab1Valid(false);
    } else {
      setIsTab1Valid(true);
    }
    if (formik.errors?.lines) {
      setIsTab3Valid(false);
    } else {
      setIsTab3Valid(true);
    }
  }, [formik]);

  const value: ContextType = {
    isEdit,
    formik,
    submit,
    uploadFiles,
    handleDeleteFile,
    handleDownloadFile,
    handlePreviewFile,
    getFileFromGI,
    loadingFile,
    setLoadingFile,
    loadingData,
    customers: formik.values.customers,
    greenInvoiceClientID: getFirstGiClient(formik.values.customers),
    isPriceQuoteComplete,
    orderID: data.orderID,
    switchToSecondTab,
    tabsRef
  };

  return (
    <PriceQuoteDetailsContext.Provider value={value}>
      {children}
    </PriceQuoteDetailsContext.Provider>
  );
}
